/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {API_URL} from '../../modules/auth/core/_requests'

type Nullable<T> = T | null | undefined

export interface PleasureSpotCompletionProduct {
  // Core identifiers (existing fields)
  child_shopify_id: number
  parent_shopify_id: number
  sku: string
  barcode?: string
  price?: number | string
  parent_title: string
  variant_title: string
  vendor: string
  product_type: string
  tags: Nullable<string>
  status: string
  parent_image_url: Nullable<string>
  variant_image_url: Nullable<string>
  admin_product_url?: Nullable<string>
  live_product_url?: Nullable<string>

  // Amazon core data
  amazon_product_id: Nullable<string>
  amazon_product_id_type: Nullable<string>
  amazon_title: Nullable<string>
  // Use existing vendor as Brand; no duplicate brand/manufacturer fields
  bullet_points: Nullable<string[]> // stored as JSON array in DB
  amazon_description: Nullable<string>

  // Sexual-Wellness specifics
  materials_surface_finish: Nullable<string>
  item_length: Nullable<number>
  item_width: Nullable<number>
  item_height: Nullable<number>
  item_diameter: Nullable<number>
  item_dimensions_unit: Nullable<string>
  item_weight: Nullable<number>
  package_weight: Nullable<number>
  weight_unit: Nullable<string>
  package_length: Nullable<number>
  package_width: Nullable<number>
  package_height: Nullable<number>
  package_dimensions_unit: Nullable<string>
  color_name: Nullable<string>
  color_map: Nullable<string>
  are_batteries_included: Nullable<boolean>
  battery_type: Nullable<string>
  number_of_batteries: Nullable<number>
  battery_weight: Nullable<number>
  battery_watt_hours: Nullable<number>
  water_resistance_rating: Nullable<string>
  speed_pattern_count: Nullable<number>
  safety_warnings: Nullable<string>
  intended_use_disclaimers: Nullable<string>

  // Recommended/optional
  iso_3533_report_url: Nullable<string>
  un38_3_battery_summary_url: Nullable<string>
  iec_62133_report_url: Nullable<string>
  sds_url: Nullable<string>
  plain_opaque_outer_packaging: Nullable<boolean>
  warranty_info: Nullable<string>
  customer_service_contact: Nullable<string>

  // Lingerie / apparel
  standardized_size_block: Nullable<Record<string, any>> // JSON object
  size_class: Nullable<string>
  size_value: Nullable<string>
  body_type: Nullable<string>
  height_type: Nullable<string>
  cup_size: Nullable<string>
  band_size: Nullable<string>
  material_composition: Nullable<string>
  gcc_flammability_doc_url: Nullable<string>
  polybag_thickness_mils: Nullable<number>
  polybag_suffocation_warning_printed: Nullable<boolean>
  sustainable_claims: Nullable<string[]> // JSON array
  sustainable_certifications: Nullable<string[]> // JSON array

  // Consumables
  ingredients_list: Nullable<string>
  paraben_free: Nullable<boolean>
  phthalate_free: Nullable<boolean>
  net_content: Nullable<number>
  net_content_unit: Nullable<string>
  unit_count: Nullable<number>
  expiration_date: Nullable<string> // ISO date string
  cpsr_url: Nullable<string>

  // Regulations
  prop65_warning: Nullable<boolean>
  prop65_chemicals: Nullable<string>

  // Derived (from API or computed client-side)
  completion_score?: number
}

type FetchResponse = {
  data: PleasureSpotCompletionProduct[]
  totalItems: number
}

const REQUIRED_FIELDS_BASE = [
  'sku',
  'amazon_product_id',
  'amazon_product_id_type',
  'amazon_title',
  'vendor', // treat vendor as Brand
  'bullet_points',
]

const REQUIRED_FIELDS_SEXUAL_WELLNESS = [
  'materials_surface_finish',
  'item_length',
  'item_width',
  'item_height',
  'item_weight',
  'color_name',
  'are_batteries_included',
  'water_resistance_rating',
]

const REQUIRED_FIELDS_LINGERIE = [
  'size_class',
  'size_value',
  'material_composition',
]

const REQUIRED_FIELDS_CONSUMABLES = [
  'ingredients_list',
  'net_content',
  'net_content_unit',
  'expiration_date',
]

function productSignals(product: PleasureSpotCompletionProduct) {
  const typeLower = (product.product_type || '').toLowerCase()
  const titleLower = (product.parent_title || '').toLowerCase()
  const tagsLower = (product.tags || '').toLowerCase()
  return {typeLower, titleLower, tagsLower}
}

function isSexualWellness(product: PleasureSpotCompletionProduct): boolean {
  const {typeLower, titleLower, tagsLower} = productSignals(product)
  const tokens = ['vibrator', 'lube', 'lubricant', 'bondage', 'adult', 'toy', 'sex', 'game']
  return tokens.some((t) => typeLower.includes(t) || titleLower.includes(t) || tagsLower.includes(t))
}

function isLingerie(product: PleasureSpotCompletionProduct): boolean {
  const {typeLower, titleLower, tagsLower} = productSignals(product)
  const tokens = ['lingerie', 'dress', 'bra', 'panty', 'garter']
  return tokens.some((t) => typeLower.includes(t) || titleLower.includes(t) || tagsLower.includes(t))
}

function isConsumable(product: PleasureSpotCompletionProduct): boolean {
  const {typeLower, titleLower, tagsLower} = productSignals(product)
  const tokens = ['lube', 'cream', 'cleaner', 'consumable', 'oil', 'gel']
  return tokens.some((t) => typeLower.includes(t) || titleLower.includes(t) || tagsLower.includes(t))
}

function computeCompletionScore(product: PleasureSpotCompletionProduct): number {
  const fields = new Set<string>(REQUIRED_FIELDS_BASE)

  if (isSexualWellness(product)) REQUIRED_FIELDS_SEXUAL_WELLNESS.forEach((f) => fields.add(f))
  if (isLingerie(product)) REQUIRED_FIELDS_LINGERIE.forEach((f) => fields.add(f))
  if (isConsumable(product)) REQUIRED_FIELDS_CONSUMABLES.forEach((f) => fields.add(f))

  const fieldList = Array.from(fields)
  const filled = fieldList.filter((key) => {
    if (key === 'amazon_title') {
      // consider filled if either amazon_title or parent_title is present
      const at = (product as any)['amazon_title']
      const pt = (product as any)['parent_title']
      return (at && String(at).trim() !== '') || (pt && String(pt).trim() !== '')
    }
    const value: any = (product as any)[key]
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return value != null && Object.keys(value).length > 0
    return value !== null && value !== undefined && String(value).trim() !== ''
  })
  const score = Math.round((filled.length / fieldList.length) * 100)
  return Number.isFinite(score) ? score : 0
}

function computeMissingFields(product: PleasureSpotCompletionProduct): string[] {
  const fields = new Set<string>(REQUIRED_FIELDS_BASE)
  if (isSexualWellness(product)) REQUIRED_FIELDS_SEXUAL_WELLNESS.forEach((f) => fields.add(f))
  if (isLingerie(product)) REQUIRED_FIELDS_LINGERIE.forEach((f) => fields.add(f))
  if (isConsumable(product)) REQUIRED_FIELDS_CONSUMABLES.forEach((f) => fields.add(f))

  const list = Array.from(fields)
  return list.filter((key) => {
    if (key === 'amazon_title') {
      const at = (product as any)['amazon_title']
      const pt = (product as any)['parent_title']
      return !((at && String(at).trim() !== '') || (pt && String(pt).trim() !== ''))
    }
    const value: any = (product as any)[key]
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return !(value != null && Object.keys(value).length > 0)
    return value === null || value === undefined || String(value).trim() === ''
  })
}

type PendingEdits = Record<string, any>

export const PleasureSpotCompletionPage: FC = () => {
  const [items, setItems] = useState<PleasureSpotCompletionProduct[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState<string>('')
  const [vendor, setVendor] = useState<string>('')
  const [productType, setProductType] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(25)
  const [total, setTotal] = useState<number>(0)
  const [pending, setPending] = useState<Record<number, PendingEdits>>({})
  const [editingRowId, setEditingRowId] = useState<number | null>(null)
  const [modalRowId, setModalRowId] = useState<number | null>(null)
  const [modalForm, setModalForm] = useState<Partial<PleasureSpotCompletionProduct>>({})

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const params: any = {
        page,
        pageSize,
        sortField: 'parent_title',
        sortOrder: 'ASC',
      }
      if (search) params.title = search
      if (vendor) params.vendor = vendor
      if (productType) params.productType = productType

      const resp = await axios.get<FetchResponse>(
        `${API_URL}/api/pleasurespot/completion/products`,
        {params}
      )
      const withScores = resp.data.data.map((p) => ({
        ...p,
        completion_score: p.completion_score ?? computeCompletionScore(p),
      }))
      setItems(withScores)
      setTotal(resp.data.totalItems)
    } catch (e) {
      console.error(e)
      setError('Failed to load products')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchData, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, vendor, productType, page, pageSize])

  const vendors = useMemo(() => {
    const setV = new Set<string>()
    items.forEach((i) => i.vendor && setV.add(i.vendor))
    return Array.from(setV).sort()
  }, [items])

  const productTypes = useMemo(() => {
    const setT = new Set<string>()
    items.forEach((i) => i.product_type && setT.add(i.product_type))
    return Array.from(setT).sort()
  }, [items])

  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  const updatePending = (id: number, field: string, value: any) => {
    setPending((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }))
  }

  const saveRow = async (id: number) => {
    const changes = pending[id]
    if (!changes || Object.keys(changes).length === 0) return
    try {
      await axios.put(`${API_URL}/api/pleasurespot/completion/products/${id}` as string, changes)
      setPending((prev) => {
        const copy = {...prev}
        delete copy[id]
        return copy
      })
      // optimistic update
      setItems((prev) =>
        prev.map((row) => (row.child_shopify_id === id ? {...row, ...changes} : row))
      )
    } catch (e) {
      console.error(e)
      alert('Failed to save changes')
    }
  }

  const cancelRow = (id: number) => {
    setPending((prev) => {
      const copy = {...prev}
      delete copy[id]
      return copy
    })
  }

  const openModal = (row: PleasureSpotCompletionProduct) => {
    const inferredColor = row.color_name || (row.variant_title && row.variant_title !== 'Default Title' ? row.variant_title : '')
    setModalRowId(row.child_shopify_id)
    setModalForm({
      ...row,
      amazon_title: row.amazon_title ?? row.parent_title ?? '',
      bullet_points: Array.isArray(row.bullet_points) ? row.bullet_points : [],
      color_name: inferredColor,
    })
  }

  const closeModal = () => {
    setModalRowId(null)
    setModalForm({})
  }

  const setModalField = (
    field: keyof PleasureSpotCompletionProduct,
    value: any
  ) => setModalForm((prev) => ({...prev, [field]: value}))

  const saveModal = async () => {
    if (modalRowId == null) return
    // compute diff against current item
    const original = items.find((i) => i.child_shopify_id === modalRowId)
    if (!original) return
    const changes: Record<string, any> = {}
    Object.keys(modalForm).forEach((k) => {
      const key = k as keyof PleasureSpotCompletionProduct
      const newVal = (modalForm as any)[key]
      const oldVal = (original as any)[key]
      const equal = JSON.stringify(newVal) === JSON.stringify(oldVal)
      if (!equal) changes[key] = newVal
    })
    if (Object.keys(changes).length === 0) return closeModal()
    try {
      await axios.put(`${API_URL}/api/pleasurespot/completion/products/${modalRowId}` as string, changes)
      setItems((prev) => prev.map((r) => (r.child_shopify_id === modalRowId ? {...r, ...changes} : r)))
      closeModal()
    } catch (e) {
      console.error(e)
      alert('Failed to save changes')
    }
  }

  const renderEditable = (
    row: PleasureSpotCompletionProduct,
    field: keyof PleasureSpotCompletionProduct,
    placeholder?: string
  ) => {
    const id = row.child_shopify_id
    const value = (pending[id]?.[field as string] ?? (row as any)[field]) as any
    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      updatePending(id, field as string, e.target.value)
    return editingRowId === id ? (
      <input
        type='text'
        className='form-control form-control-sm'
        value={value ?? ''}
        placeholder={placeholder}
        onChange={onChange}
      />
    ) : (
      <span>{(value ?? '') as string}</span>
    )
  }

  const renderNumber = (
    row: PleasureSpotCompletionProduct,
    field: keyof PleasureSpotCompletionProduct,
    step = 0.01
  ) => {
    const id = row.child_shopify_id
    const raw = pending[id]?.[field as string] ?? (row as any)[field]
    const value = raw ?? ''
    return editingRowId === id ? (
      <input
        type='number'
        className='form-control form-control-sm'
        value={value}
        step={step}
        onChange={(e) => updatePending(id, field as string, e.target.value === '' ? null : Number(e.target.value))}
      />
    ) : (
      <span>{value === '' || value === null || value === undefined ? '' : String(value)}</span>
    )
  }

  const renderBool = (
    row: PleasureSpotCompletionProduct,
    field: keyof PleasureSpotCompletionProduct
  ) => {
    const id = row.child_shopify_id
    const raw = pending[id]?.[field as string]
    const checked = (raw ?? (row as any)[field]) ?? false
    return editingRowId === id ? (
      <input
        type='checkbox'
        className='form-check-input'
        checked={!!checked}
        onChange={(e) => updatePending(id, field as string, e.target.checked)}
      />
    ) : (
      <span>{checked ? 'Yes' : ''}</span>
    )
  }

  if (loading && items.length === 0) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return <div className='alert alert-danger ms-3 me-3'>{error}</div>
  }

  return (
    <div className='p-3'>
      <div className='card mb-4 shadow-sm'>
        <div className='card-body'>
          <div className='row g-3 align-items-end'>
            <div className='col-md-4'>
              <label className='form-label small fw-semibold text-muted mb-1'>Search Title</label>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder='Search by product title...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label small fw-semibold text-muted mb-1'>Vendor</label>
              <select
                className='form-select form-select-sm'
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
              >
                <option value=''>All Vendors</option>
                {vendors.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-3'>
              <label className='form-label small fw-semibold text-muted mb-1'>Product Type</label>
              <select
                className='form-select form-select-sm'
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
              >
                <option value=''>All Types</option>
                {productTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-2 text-end'>
              <div className='small text-muted'>
                {start}-{end} of {total}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='table-responsive'>
        <table className='table table-sm align-middle table-row-dashed gy-2 gx-2'>
          <thead>
            <tr className='text-muted fw-semibold'>
              <th style={{minWidth: 70}}></th>
              <th style={{minWidth: 120}}>Score</th>
              <th style={{minWidth: 120}}>Image</th>
              <th style={{minWidth: 220}}>Title</th>
              <th style={{minWidth: 110}}>SKU</th>
              <th style={{minWidth: 160}}>Brand</th>
              <th style={{minWidth: 140}}>Amazon ID</th>
              <th style={{minWidth: 120}}>ID Type</th>
              <th style={{minWidth: 200}}>Bullet Points</th>
              <th style={{minWidth: 140}}>Color</th>
              <th style={{minWidth: 220}}>Materials</th>
              <th style={{minWidth: 160}}>Item Dim. (L×W×H)</th>
              <th style={{minWidth: 120}}>Weight</th>
              <th style={{minWidth: 110}}>Batteries</th>
              <th style={{minWidth: 120}}>Water</th>
              <th style={{minWidth: 140}}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const id = row.child_shopify_id
              const bp = (pending[id]?.bullet_points ?? row.bullet_points) ?? []
              const bpText = Array.isArray(bp) ? bp.join(' | ') : ''
              const onBpChange = (val: string) =>
                updatePending(id, 'bullet_points', val.split('|').map((s) => s.trim()).filter(Boolean))
              const score = computeCompletionScore({
                ...row,
                ...(pending[id] || {}),
              })
              const missing = computeMissingFields({
                ...row,
                ...(pending[id] || {}),
              })
              return (
                <tr key={id}>
                  <td>
                    <button className='btn btn-sm btn-light' onClick={() => openModal(row)} title='View all data'>
                      View
                    </button>
                  </td>
                  <td>
                    <div className='d-flex align-items-center gap-2'>
                      <div
                        className='progress w-100'
                        style={{height: 8}}
                        title={missing.length ? `Missing: ${missing.join(', ')}` : 'Complete'}
                      >
                        <div
                          className={`progress-bar ${score >= 80 ? 'bg-success' : score >= 50 ? 'bg-warning' : 'bg-danger'}`}
                          role='progressbar'
                          style={{width: `${score}%`}}
                          aria-valuenow={score}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                      <span className='fw-semibold small' style={{minWidth: 36}} title={missing.length ? `Missing: ${missing.join(', ')}` : 'Complete'}>
                        {score}%
                      </span>
                    </div>
                  </td>
                  <td>
                    {(row.variant_image_url || row.parent_image_url) && (
                      <img
                        src={(row.variant_image_url || row.parent_image_url) as string}
                        alt={row.variant_title || row.parent_title}
                        style={{width: 64, height: 64, objectFit: 'contain'}}
                      />
                    )}
                  </td>
                  <td style={{maxWidth: 320}}>
                    {editingRowId === id ? (
                      renderEditable(row, 'amazon_title' as any, row.parent_title)
                    ) : (
                      <span>{(row as any).amazon_title || row.parent_title}</span>
                    )}
                  </td>
                  <td>{row.sku}</td>
                  <td>{row.vendor || ''}</td>
                  <td>{renderEditable(row, 'amazon_product_id')}</td>
                  <td>
                    {editingRowId === id ? (
                      <select
                        className='form-select form-select-sm'
                        value={(pending[id]?.amazon_product_id_type ?? row.amazon_product_id_type) || ''}
                        onChange={(e) => updatePending(id, 'amazon_product_id_type', e.target.value)}
                      >
                        <option value=''>Select</option>
                        <option value='UPC'>UPC</option>
                        <option value='EAN'>EAN</option>
                        <option value='GTIN'>GTIN</option>
                        <option value='ASIN'>ASIN</option>
                      </select>
                    ) : (
                      <span>{row.amazon_product_id_type || ''}</span>
                    )}
                  </td>
                  <td>
                    {editingRowId === id ? (
                      <input
                        type='text'
                        className='form-control form-control-sm'
                        value={bpText}
                        placeholder='point 1 | point 2 | point 3'
                        onChange={(e) => onBpChange(e.target.value)}
                      />
                    ) : (
                      <span>{bpText}</span>
                    )}
                  </td>
                  <td>
                    {renderEditable(
                      row,
                      'color_name',
                      // show variant_title as a helpful hint if color_name is empty
                      row.color_name || (row.variant_title && row.variant_title !== 'Default Title' ? row.variant_title : '')
                    )}
                  </td>
                  <td>{renderEditable(row, 'materials_surface_finish')}</td>
                  <td>
                    <div className='d-flex gap-1'>
                      {renderNumber(row, 'item_length')}
                      {renderNumber(row, 'item_width')}
                      {renderNumber(row, 'item_height')}
                    </div>
                  </td>
                  <td>{renderNumber(row, 'item_weight')}</td>
                  <td className='text-center'>{renderBool(row, 'are_batteries_included')}</td>
                  <td>{renderEditable(row, 'water_resistance_rating')}</td>
                  <td className='text-end'>
                    <div className='btn-group btn-group-sm'>
                      {editingRowId === id ? (
                        <>
                          <button className='btn btn-primary' onClick={() => saveRow(id)} disabled={!pending[id]}>
                            Save
                          </button>
                          <button
                            className='btn btn-light'
                            onClick={() => {
                              cancelRow(id)
                              setEditingRowId(null)
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button className='btn btn-light' onClick={() => setEditingRowId(id)} title={missing.length ? `Missing: ${missing.join(', ')}` : 'Edit'}>
                            Quick Edit
                          </button>
                          <button className='btn btn-primary' onClick={() => openModal(row)}>
                            Edit
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className='d-flex justify-content-between align-items-center mt-3'>
        <div className='d-flex align-items-center gap-2'>
          <span className='text-muted small'>Rows per page</span>
          <select
            className='form-select form-select-sm'
            style={{width: 80}}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value))
              setPage(1)
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className='d-flex align-items-center gap-2'>
          <button className='btn btn-sm btn-light' disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span className='small text-muted'>Page {page}</span>
          <button
            className='btn btn-sm btn-light'
            disabled={end >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {modalRowId !== null && (() => {
        const current = items.find((i) => i.child_shopify_id === modalRowId)
        if (!current) return null
        const product = {...current, ...(modalForm as any)} as PleasureSpotCompletionProduct
        const missing = computeMissingFields(product)
        const score = computeCompletionScore(product)
        const isRequired = (field: string) => {
          const base = new Set(REQUIRED_FIELDS_BASE)
          const typeLower = (product.product_type || '').toLowerCase()
          const fields = new Set<string>(base)
          if (/(vibrator|lube|lubricant|bondage|adult|toy)/.test(typeLower)) REQUIRED_FIELDS_SEXUAL_WELLNESS.forEach((f) => fields.add(f))
          if (/(lingerie|dress|bra|panty)/.test(typeLower)) REQUIRED_FIELDS_LINGERIE.forEach((f) => fields.add(f))
          if (/(lube|cream|cleaner|consumable)/.test(typeLower)) REQUIRED_FIELDS_CONSUMABLES.forEach((f) => fields.add(f))
          return fields.has(field)
        }
        const inputCls = (field: keyof PleasureSpotCompletionProduct) =>
          `form-control form-control-sm ${isRequired(field) && missing.includes(field) ? 'is-invalid' : ''}`
        return (
          <>
            <div className='modal fade show' style={{display: 'block'}} aria-modal='true' role='dialog'>
              <div className='modal-dialog modal-xl modal-dialog-scrollable'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <div>
                      <h5 className='modal-title'>Edit Product</h5>
                      <div className='text-muted small'>{product.parent_title}</div>
                    </div>
                    <div className='d-flex align-items-center gap-3'>
                      <div className='d-flex align-items-center gap-2' title={missing.length ? `Missing: ${missing.join(', ')}` : 'Complete'}>
                        <div className='progress' style={{width: 120, height: 8}}>
                          <div className={`progress-bar ${score >= 80 ? 'bg-success' : score >= 50 ? 'bg-warning' : 'bg-danger'}`} style={{width: `${score}%`}} />
                        </div>
                        <span className='fw-semibold small'>{score}%</span>
                      </div>
                      <button type='button' className='btn btn-icon btn-sm btn-light' onClick={closeModal} aria-label='Close'>
                        <i className='fa-solid fa-xmark'></i>
                      </button>
                    </div>
                  </div>
                  <div className='modal-body'>
                    {missing.length > 0 && (
                      <div className='alert alert-warning py-2'>
                        <div className='fw-semibold mb-1'>Required fields missing</div>
                        <div className='d-flex flex-wrap gap-2'>
                          {missing.map((m) => (
                            <span key={m} className='badge bg-light text-dark border'>{m}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <label className='form-label small'>Amazon Title {isRequired('amazon_title') && <span className='text-danger'>*</span>}</label>
                        <input className={inputCls('amazon_title')} value={String(modalForm.amazon_title ?? product.amazon_title ?? '')}
                          placeholder={product.parent_title}
                          onChange={(e) => setModalField('amazon_title', e.target.value)} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>SKU</label>
                        <input className='form-control form-control-sm' value={product.sku} disabled />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Variant</label>
                        <input className='form-control form-control-sm' value={product.variant_title || ''} disabled />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Brand</label>
                        <input className='form-control form-control-sm' value={product.vendor || ''} disabled />
                      </div>

                      <div className='col-md-4'>
                        <label className='form-label small'>Amazon Product ID {isRequired('amazon_product_id') && <span className='text-danger'>*</span>}</label>
                        <input className={inputCls('amazon_product_id')} value={String(modalForm.amazon_product_id ?? product.amazon_product_id ?? '')}
                          onChange={(e) => setModalField('amazon_product_id', e.target.value)} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>ID Type {isRequired('amazon_product_id_type') && <span className='text-danger'>*</span>}</label>
                        <select className={`form-select form-select-sm ${isRequired('amazon_product_id_type') && missing.includes('amazon_product_id_type') ? 'is-invalid' : ''}`}
                          value={String(modalForm.amazon_product_id_type ?? product.amazon_product_id_type ?? '')}
                          onChange={(e) => setModalField('amazon_product_id_type', e.target.value)}>
                          <option value=''>Select</option>
                          <option value='UPC'>UPC</option>
                          <option value='EAN'>EAN</option>
                          <option value='GTIN'>GTIN</option>
                          <option value='ASIN'>ASIN</option>
                        </select>
                      </div>
                      <div className='col-md-5'>
                        <label className='form-label small'>Color Name</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.color_name ?? product.color_name ?? '')}
                          placeholder={product.variant_title && product.variant_title !== 'Default Title' ? product.variant_title : ''}
                          onChange={(e) => setModalField('color_name', e.target.value)} />
                      </div>

                      <div className='col-12'>
                        <label className='form-label small'>Bullet Points {isRequired('bullet_points') && <span className='text-danger'>*</span>}</label>
                        <input className={`form-control form-control-sm ${isRequired('bullet_points') && missing.includes('bullet_points') ? 'is-invalid' : ''}`}
                          value={Array.isArray(modalForm.bullet_points ?? product.bullet_points) ? (modalForm.bullet_points ?? product.bullet_points ?? []).join(' | ') : ''}
                          placeholder='point 1 | point 2 | point 3'
                          onChange={(e) => setModalField('bullet_points', e.target.value.split('|').map((s) => s.trim()).filter(Boolean))} />
                      </div>

                      <div className='col-md-3'>
                        <label className='form-label small'>Barcode</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.barcode ?? product.barcode ?? '')}
                          onChange={(e) => setModalField('barcode', e.target.value)} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Price</label>
                        <input type='number' step='0.01' className='form-control form-control-sm'
                          value={typeof (modalForm.price ?? product.price) === 'string' ? (modalForm.price ?? product.price ?? '') : (modalForm.price ?? product.price ?? '')}
                          onChange={(e) => setModalField('price', e.target.value === '' ? null : Number(e.target.value))} />
                      </div>

                      <div className='col-md-4'>
                        <label className='form-label small'>Materials / Finish</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.materials_surface_finish ?? product.materials_surface_finish ?? '')}
                          onChange={(e) => setModalField('materials_surface_finish', e.target.value)} />
                      </div>
                      <div className='col-md-2'>
                        <label className='form-label small'>Length</label>
                        <input type='number' step='0.01' className='form-control form-control-sm'
                          value={modalForm.item_length ?? product.item_length ?? ''}
                          onChange={(e) => setModalField('item_length', e.target.value === '' ? null : Number(e.target.value))} />
                      </div>
                      <div className='col-md-2'>
                        <label className='form-label small'>Width</label>
                        <input type='number' step='0.01' className='form-control form-control-sm'
                          value={modalForm.item_width ?? product.item_width ?? ''}
                          onChange={(e) => setModalField('item_width', e.target.value === '' ? null : Number(e.target.value))} />
                      </div>
                      <div className='col-md-2'>
                        <label className='form-label small'>Height</label>
                        <input type='number' step='0.01' className='form-control form-control-sm'
                          value={modalForm.item_height ?? product.item_height ?? ''}
                          onChange={(e) => setModalField('item_height', e.target.value === '' ? null : Number(e.target.value))} />
                      </div>
                      <div className='col-md-2'>
                        <label className='form-label small'>Dimensions Unit</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.item_dimensions_unit ?? product.item_dimensions_unit ?? '')}
                          onChange={(e) => setModalField('item_dimensions_unit', e.target.value)} />
                      </div>
                      <div className='col-md-2'>
                        <label className='form-label small'>Weight</label>
                        <input type='number' step='0.001' className='form-control form-control-sm'
                          value={modalForm.item_weight ?? product.item_weight ?? ''}
                          onChange={(e) => setModalField('item_weight', e.target.value === '' ? null : Number(e.target.value))} />
                      </div>
                      <div className='col-md-2'>
                        <label className='form-label small'>Weight Unit</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.weight_unit ?? product.weight_unit ?? '')}
                          onChange={(e) => setModalField('weight_unit', e.target.value)} />
                      </div>

                      <div className='col-md-4'>
                        <label className='form-label small'>Batteries Included</label>
                        <div className='form-check'>
                          <input className='form-check-input' type='checkbox'
                            checked={!!(modalForm.are_batteries_included ?? product.are_batteries_included)}
                            onChange={(e) => setModalField('are_batteries_included', e.target.checked)} />
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <label className='form-label small'>Water Resistance</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.water_resistance_rating ?? product.water_resistance_rating ?? '')}
                          onChange={(e) => setModalField('water_resistance_rating', e.target.value)} />
                      </div>

                      <div className='col-12'>
                        <label className='form-label small'>Description</label>
                        <textarea className='form-control form-control-sm' rows={4}
                          value={String(modalForm.amazon_description ?? product.amazon_description ?? '')}
                          onChange={(e) => setModalField('amazon_description', e.target.value)} />
                      </div>

                      {/* Lingerie / Consumables key fields */}
                      <div className='col-md-3'>
                        <label className='form-label small'>Size Class</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.size_class ?? product.size_class ?? '')}
                          onChange={(e) => setModalField('size_class', e.target.value)} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Size Value</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.size_value ?? product.size_value ?? '')}
                          onChange={(e) => setModalField('size_value', e.target.value)} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Material Composition</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.material_composition ?? product.material_composition ?? '')}
                          onChange={(e) => setModalField('material_composition', e.target.value)} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Ingredients</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.ingredients_list ?? product.ingredients_list ?? '')}
                          onChange={(e) => setModalField('ingredients_list', e.target.value)} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Net Content</label>
                        <input type='number' step='0.01' className='form-control form-control-sm'
                          value={modalForm.net_content ?? product.net_content ?? ''}
                          onChange={(e) => setModalField('net_content', e.target.value === '' ? null : Number(e.target.value))} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Unit</label>
                        <input className='form-control form-control-sm'
                          value={String(modalForm.net_content_unit ?? product.net_content_unit ?? '')}
                          onChange={(e) => setModalField('net_content_unit', e.target.value)} />
                      </div>
                      <div className='col-md-3'>
                        <label className='form-label small'>Expiration Date</label>
                        <input type='date' className='form-control form-control-sm'
                          value={String(modalForm.expiration_date ?? product.expiration_date ?? '')}
                          onChange={(e) => setModalField('expiration_date', e.target.value)} />
                      </div>

                      {/* Links */}
                      <div className='col-12'>
                        <div className='d-flex gap-2 align-items-center mt-2'>
                          {product.admin_product_url && (
                            <a href={product.admin_product_url} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-light'>Shopify Admin</a>
                          )}
                          {product.live_product_url && (
                            <a href={product.live_product_url} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-light'>Live Product</a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <button className='btn btn-light' onClick={closeModal}>Close</button>
                    <button className='btn btn-primary' onClick={saveModal}>Save changes</button>
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-backdrop fade show'></div>
          </>
        )
      })()}
    </div>
  )
}

export default PleasureSpotCompletionPage


